import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { resolvePhotoUrl } from '../utils/photoUrl';
import { rolesWith } from '../utils/permissions';
import PhotoGallery from '../components/PhotoGallery';

/**
 * The photo library: every photograph the bank holds, in folders.
 *
 * The reference imports attached tens of thousands of photographs to external
 * records, one record at a time. This page opens them all in one place:
 * folders by source (each reference set, field collections, external intakes,
 * lab plates and slants) or by genus. A folder is paged from the server
 * (GET /photos/library), so an 18,000-photograph folder costs one page, and
 * every photograph opens in the shared viewer with zoom.
 *
 * The reference sets (DF20, iNaturalist, Mushroom Observer; `reference` on a
 * folder) are labelled as reference-bank sources: external benchmark images,
 * kept on local storage only, that the AI reference bank and the practice
 * games use. The bank's own folders carry no such label.
 *
 * The roles match routes/photo_library.py: full lab visibility only.
 */
export const LIBRARY_ROLES = rolesWith('library:read');
export const PAGE_SIZE = 60;

export default function PhotoLibrary() {
  const { t } = useTranslation('library');
  // `t` can be a new function on every render (it is, without an i18n instance).
  // Reading it through a ref keeps the loaders stable, so a change of `t`
  // cannot refetch in a loop.
  const tRef = useRef(t);
  tRef.current = t;
  const { user } = useAuth();
  const allowed = (user?.roles || []).some(r => LIBRARY_ROLES.includes(r));

  const [group, setGroup] = useState('source');
  const [genusQuery, setGenusQuery] = useState('');
  const [folders, setFolders] = useState(null);
  const [truncated, setTruncated] = useState(null);
  const [error, setError] = useState('');
  const [folder, setFolder] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState({ items: [], total: 0, offset: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!allowed) return undefined;
    let cancelled = false;
    setError('');
    const handle = setTimeout(() => {
      api.get('/photos/library/folders', { params: { group, q: group === 'genus' ? genusQuery : undefined } })
        .then(({ data }) => {
          if (cancelled) return;
          setFolders(data.folders || []);
          setTruncated(data.truncated ? { shown: (data.folders || []).length, total: data.total_folders } : null);
        })
        .catch(() => { if (!cancelled) { setFolders([]); setError(tRef.current('loadFailed')); } });
    }, group === 'genus' && genusQuery ? 300 : 0);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [allowed, group, genusQuery]);

  const loadPage = useCallback((key, offset, q) => {
    setLoading(true);
    setError('');
    api.get('/photos/library', { params: { group, folder: key, offset, limit: PAGE_SIZE, q: q || undefined } })
      .then(({ data }) => setPage({ items: data.items || [], total: data.total || 0, offset: data.offset || 0 }))
      .catch(() => { setPage({ items: [], total: 0, offset: 0 }); setError(tRef.current('loadFailed')); })
      .finally(() => setLoading(false));
  }, [group]);

  const openFolder = (key) => {
    setFolder(key);
    setSearch('');
    loadPage(key, 0, '');
  };

  const folderLabel = (key) => {
    if (group === 'source') return t(`sources.${key}`);
    if (key === '_unidentified') return t('unidentified');
    return key.charAt(0).toUpperCase() + key.slice(1);
  };

  if (!allowed) {
    return (
      <div style={pageStyle}>
        <h2 style={titleStyle}>{t('title')}</h2>
        <p style={noticeStyle}>{t('notPermitted')}</p>
      </div>
    );
  }

  if (folder) {
    const from = page.total ? page.offset + 1 : 0;
    const to = Math.min(page.offset + PAGE_SIZE, page.total);
    const selected = (folders || []).find(f => f.key === folder);
    return (
      <div style={pageStyle}>
        <button type="button" onClick={() => setFolder(null)} title={t('tip.back')} style={linkButton}>← {t('back')}</button>
        <h2 style={titleStyle}>🗂 {folderLabel(folder)}</h2>
        {selected?.reference && (
          <>
            <ReferenceBadge t={t} />
            <p style={hintStyle}>{t('referenceNote')}</p>
            <p style={hintStyle}>{t('referenceStorage')}</p>
          </>
        )}
        <form onSubmit={e => { e.preventDefault(); loadPage(folder, 0, search.trim()); }}
              style={{ display: 'flex', gap: 8, margin: '8px 0 12px' }}>
          <input type="search" value={search} onChange={e => setSearch(e.target.value)}
                 placeholder={t('searchFolder')} aria-label={t('searchFolder')} title={t('tip.searchFolder')} style={inputStyle} />
        </form>
        {error && <p style={errorStyle}>{error}</p>}
        {loading ? <p style={hintStyle}>{t('loading')}</p> : page.items.length === 0
          ? <p style={hintStyle}>{t('empty')}</p>
          : <PhotoGallery annotatable photos={page.items} collapseAfter={Infinity} thumbWidth={140} thumbHeight={105}
                          testId="library-gallery" />}
        {page.total > 0 && (
          <div style={pagerStyle}>
            <button type="button" disabled={loading || page.offset === 0} style={pageButton} title={t('tip.previous')}
                    onClick={() => loadPage(folder, Math.max(0, page.offset - PAGE_SIZE), search.trim())}>
              {t('previous')}
            </button>
            <span style={{ fontSize: 13 }}>{t('range', { from, to, total: page.total })}</span>
            <button type="button" disabled={loading || to >= page.total} style={pageButton} title={t('tip.next')}
                    onClick={() => loadPage(folder, page.offset + PAGE_SIZE, search.trim())}>
              {t('next')}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <h2 style={titleStyle}>{t('title')}</h2>
      <p style={introStyle}>{t('intro')}</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={{ fontSize: 13, color: 'var(--c-text-body-fg)' }}>{t('groupBy')}</span>
        {['source', 'genus'].map(g => (
          <button key={g} type="button" onClick={() => { setGroup(g); setFolders(null); }}
                  aria-pressed={group === g} style={group === g ? toggleOn : toggleOff}
                  title={g === 'genus' ? t('tip.groupGenus') : t('tip.groupSource')}>
            {t(`group.${g}`)}
          </button>
        ))}
        {group === 'genus' && (
          <input type="search" value={genusQuery} onChange={e => setGenusQuery(e.target.value)}
                 placeholder={t('searchGenera')} aria-label={t('searchGenera')} title={t('tip.searchGenera')} style={{ ...inputStyle, maxWidth: 240 }} />
        )}
      </div>
      {error && <p style={errorStyle}>{error}</p>}
      {truncated && <p style={hintStyle}>{t('truncated', truncated)}</p>}
      {(folders || []).some(f => f.reference) && (
        <p style={hintStyle} data-testid="library-reference-note">{t('referenceStorage')}</p>
      )}
      {folders === null
        ? <p style={hintStyle}>{t('loading')}</p>
        : folders.length === 0
          ? <p style={hintStyle}>{t('noFolders')}</p>
          : (
            <div style={folderGrid}>
              {folders.map(f => (
                <button key={f.key} type="button" data-testid="library-folder" onClick={() => openFolder(f.key)}
                        title={t('tip.folder')} style={folderCard}>
                  {f.cover_url
                    ? <img src={resolvePhotoUrl(f.cover_url)} alt="" loading="lazy" style={coverStyle} />
                    : <div style={{ ...coverStyle, background: 'var(--c-bg-muted)' }} />}
                  <span style={{ fontWeight: 600, color: 'var(--c-text-fg)' }}>🗂 {folderLabel(f.key)}</span>
                  {f.reference && <ReferenceBadge t={t} />}
                  <span style={{ fontSize: 12, color: 'var(--c-text-muted-fg)' }}>
                    {t('folderCounts', { photos: f.photo_count, records: f.record_count })}
                  </span>
                </button>
              ))}
            </div>
          )}
    </div>
  );
}

function ReferenceBadge({ t }) {
  return (
    <span data-testid="library-reference-badge" title={t('tip.referenceBadge')} style={referenceBadgeStyle}>
      {t('referenceBadge')}
    </span>
  );
}

const pageStyle = { maxWidth: 1180, margin: '0 auto', padding: '8px 4px' };
const titleStyle = { margin: '0 0 6px', color: 'var(--c-primary-fg)' };
const introStyle = { fontSize: 14, color: 'var(--c-text-body-fg)', margin: '0 0 14px', lineHeight: 1.5 };
const hintStyle = { fontSize: 13, color: 'var(--c-text-muted-fg)', margin: '4px 0 10px' };
const noticeStyle = { fontSize: 13, color: 'var(--c-warning-text-fg)', background: 'var(--c-warning-soft)', borderRadius: 6, padding: '8px 12px' };
const errorStyle = { fontSize: 13, color: 'var(--c-danger-fg)', background: 'var(--c-danger-soft)', borderRadius: 6, padding: '8px 12px' };
const inputStyle = { flex: 1, padding: '6px 10px', border: '1px solid var(--c-border-strong)', borderRadius: 6, fontSize: 14 };
const folderGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 12 };
const folderCard = {
  display: 'flex', flexDirection: 'column', gap: 4, padding: 8, textAlign: 'start', background: 'var(--c-surface)',
  border: '1px solid var(--c-border)', borderRadius: 8, cursor: 'pointer',
};
const coverStyle = { width: '100%', height: 130, objectFit: 'cover', borderRadius: 6, display: 'block' };
const linkButton = { background: 'none', border: 'none', color: 'var(--c-primary-fg)', cursor: 'pointer', padding: 0, fontSize: 14 };
const pagerStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, margin: '14px 0' };
const pageButton = {
  background: 'var(--c-bg-muted)', border: '1px solid var(--c-border-strong)', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 13,
};
const toggleOn = { background: 'var(--c-primary)', color: 'var(--c-on-accent)', border: '1px solid var(--c-primary)', borderRadius: 16, padding: '4px 12px', cursor: 'pointer' };
const toggleOff = { background: 'var(--c-surface)', color: 'var(--c-primary-fg)', border: '1px solid var(--c-primary)', borderRadius: 16, padding: '4px 12px', cursor: 'pointer' };
const referenceBadgeStyle = {
  alignSelf: 'flex-start', display: 'inline-block', fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 10,
  background: 'var(--c-info-soft)', color: 'var(--c-info-fg)',
};
